import { API_BASE_URL } from "../../config/apiConfig";
import { toastManager } from "../../utils/toastManager";

/**
 * Базовые интерфейсы и типы
 */
export interface BaseFetchOptions extends RequestInit {
  params?: Record<string, string>;
  suppressErrorToast?: boolean;
}

export interface ApiError {
  message: string;
  status: number;
}

export class ApiClientError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
  }
}

/**
 * Базовая функция для выполнения HTTP запросов
 * Используется как apiClient, так и authInterceptor
 */
export async function baseFetch<T>(
  endpoint: string,
  options: BaseFetchOptions = {},
): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE_URL}${endpoint}`;

  if (params) {
    const queryString = new URLSearchParams(params).toString();
    url += `?${queryString}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...fetchOptions.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Try to get the most informative error message
    const errorMessage =
      errorData.message ||
      errorData.error ||
      response.statusText ||
      `HTTP ${response.status} Error`;

    const error = new ApiClientError(errorMessage, response.status);

    // Log all API errors to console
    console.error(
      `API Error [${response.status}] ${options.method || "GET"} ${endpoint}:`,
      errorMessage,
    );

    // Show toast notification for all API errors (unless suppressed)
    // Don't show toast for 401 errors (handled by auth interceptor)
    if (!options.suppressErrorToast && response.status !== 401) {
      toastManager.error(errorMessage);
    }

    throw error;
  }

  const contentLengthHeader = response.headers.get("content-length");
  if (
    response.status === 204 ||
    (contentLengthHeader !== null && Number(contentLengthHeader) === 0)
  ) {
    return undefined as T;
  }

  return response.json();
}
